import {
  Alert,
  AlertDescription,
  Button,
  CopyButton,
  Label,
} from '@tinct/web-components';
import { ReadSsoConnection } from '@tinct/core-api-models';
import {
  usePatchSsoConnection,
  useSaveSsoConnection,
} from '@tinct/core-api';
import { format } from 'date-fns';
import { ExternalLinkIcon, InfoIcon } from 'lucide-react';
import { toast } from 'sonner';

export type SsoServiceProviderValuesProps = {
  organizationId: string;
  /** The workspace's connection, whatever its protocol; `undefined` while it has none. */
  connection: ReadSsoConnection | undefined;
};

type ValueRowProps = {
  label: string;
  value: string | null | undefined;
};

const ValueRow = ({ label, value }: ValueRowProps) => (
  <div className="flex flex-col gap-1.5">
    <Label>{label}</Label>
    <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-3">
      <span className="break-all font-mono text-xs">{value}</span>
      {value && <CopyButton value={value} />}
    </div>
  </div>
);

/** A file the browser saves from memory: the PEM is short, no request needed. */
const pemDataUrl = (pem: string) =>
  `data:application/x-pem-file;charset=utf-8,${encodeURIComponent(pem)}`;

/**
 * The values an admin registers on the identity provider's side, and the platform's signing
 * certificate for a provider that wants it pasted. They carry the connection's id, so the API only
 * has them once the connection exists: before that, one click saves a draft with the protocol alone
 * (the API accepts it) and the values appear, for the providers that ask for them first (Entra).
 */
export function SsoServiceProviderValues({
  organizationId,
  connection,
}: SsoServiceProviderValuesProps) {
  const onError = (error: { error: string }) => toast.error(error.error);
  const saveConnection = useSaveSsoConnection({ onError });
  const patchConnection = usePatchSsoConnection({ onError });
  const isIssuing = saveConnection.isPending || patchConnection.isPending;

  const issueValues = () => {
    const data = { protocol: 'saml' as const };
    // A connection on the other protocol is switched rather than replaced, so the settings the form
    // does not show (role, session length, just-in-time creation) keep their value.
    if (connection?.id) {
      patchConnection.mutate({ organizationId, data });
      return;
    }
    saveConnection.mutate({ organizationId, data });
  };

  const isSamlConnection =
    connection?.protocol === 'saml' && !!connection.spEntityId;

  if (!isSamlConnection) {
    return (
      <Alert>
        <InfoIcon />
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>
            The values to register on your provider's side — entity ID,
            sign-on URL, metadata and our signing certificate — are issued with
            the connection. Get them first if your provider asks for them
            before it gives you anything (Microsoft Entra ID does); otherwise
            describe your provider below and they appear with the save.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={issueValues}
            isLoading={isIssuing}
          >
            Get the values for my provider
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <span className="font-semibold">Values for your identity provider</span>
        <span className="text-sm text-muted-foreground">
          Register these in the SAML application of your provider. The metadata
          document holds them all, our signing certificate included.
        </span>
      </div>

      <ValueRow
        label="Service provider entity ID (audience)"
        value={connection.spEntityId}
      />
      <ValueRow label="Single sign-on URL (ACS)" value={connection.spAcsUrl} />

      {connection.spMetadataUrl && (
        <div className="flex flex-col gap-1.5">
          <Label>Service provider metadata</Label>
          <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-3">
            <span className="break-all font-mono text-xs">
              {connection.spMetadataUrl}
            </span>
            <CopyButton value={connection.spMetadataUrl} />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a
                href={connection.spMetadataUrl}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLinkIcon className="size-4" />
                Open
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              {/* A public document: the browser downloads it straight from the API. */}
              <a
                href={connection.spMetadataUrl}
                download="tinct-sp-metadata.xml"
              >
                Download
              </a>
            </Button>
          </div>
        </div>
      )}

      {connection.spCertificate && (
        <div className="flex flex-col gap-1.5">
          <Label>Our signing certificate</Label>
          <span className="text-sm text-muted-foreground">
            Only for a provider that verifies signed requests and asks for the
            certificate itself rather than reading the metadata
            {connection.spCertificateNotAfter &&
              `. Valid until ${format(connection.spCertificateNotAfter, 'dd/MM/yyyy')}`}
            .
          </span>
          <div className="flex items-center gap-2">
            <CopyButton value={connection.spCertificate} />
            <Button variant="outline" size="sm" asChild>
              <a
                href={pemDataUrl(connection.spCertificate)}
                download="tinct-sp-certificate.pem"
              >
                Download
              </a>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
